/**
 * Configuração do react-i18next.
 *
 * Estrutura conforme spec §5:
 *   - fallbackLng: 'pt-BR'
 *   - defaultNS: 'common'
 *   - interpolation.escapeValue: false (React já escapa)
 *   - Detecção de idioma do browser via i18next-browser-languagedetector
 *
 * Os 9 namespaces são carregados de forma eager (import estático) — o app
 * é desktop-only e cada arquivo é pequeno, então pré-carregar tudo evita
 * suspense/flicker nas trocas de página.
 *
 * Para adicionar um novo idioma no futuro, basta criar
 * `src/i18n/locales/<lang>/<namespace>.json` e adicionar a chave em
 * `resources`. O LanguageDetector escolhe automaticamente o idioma do
 * browser quando houver tradução disponível.
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

import ptBRCommon from './locales/pt-BR/common.json'
import ptBRAuth from './locales/pt-BR/auth.json'
import ptBRTestCases from './locales/pt-BR/testCases.json'
import ptBRTestSuites from './locales/pt-BR/testSuites.json'
import ptBRExecutions from './locales/pt-BR/executions.json'
import ptBRSchedules from './locales/pt-BR/schedules.json'
import ptBRBilling from './locales/pt-BR/billing.json'
import ptBRAdmin from './locales/pt-BR/admin.json'
import ptBRPreview from './locales/pt-BR/preview.json'
import ptBRUsers from './locales/pt-BR/users.json'
import ptBRProfiles from './locales/pt-BR/profiles.json'
import ptBRApiKeys from './locales/pt-BR/apiKeys.json'
import ptBRVoiceSamples from './locales/pt-BR/voiceSamples.json'
import ptBRDashboard from './locales/pt-BR/dashboard.json'
import ptBRImports from './locales/pt-BR/imports.json'
import ptBRDatasets from './locales/pt-BR/datasets.json'
import ptBRFunctions from './locales/pt-BR/functions.json'
import ptBRAppProfiles from './locales/pt-BR/appProfiles.json'
import ptBRJourneyProfiles from './locales/pt-BR/journeyProfiles.json'
import ptBRDevices from './locales/pt-BR/devices.json'
import ptBRObservability from './locales/pt-BR/observability.json'

/** Idioma usado como fallback quando o detector não encontra correspondência. */
export const DEFAULT_LANGUAGE = 'pt-BR' as const

/** Lista canônica de namespaces — exportada para uso em testes e tipos. */
export const NAMESPACES = [
  'common',
  'auth',
  'testCases',
  'testSuites',
  'executions',
  'schedules',
  'billing',
  'admin',
  'preview',
  'users',
  'profiles',
  'apiKeys',
  'voiceSamples',
  'dashboard',
  'imports',
  'datasets',
  'functions',
  'appProfiles',
  'journeyProfiles',
  'devices',
  'observability',
] as const

export type Namespace = (typeof NAMESPACES)[number]

/** Recursos de tradução agrupados por idioma e namespace. */
export const resources = {
  'pt-BR': {
    common: ptBRCommon,
    auth: ptBRAuth,
    testCases: ptBRTestCases,
    testSuites: ptBRTestSuites,
    executions: ptBRExecutions,
    schedules: ptBRSchedules,
    billing: ptBRBilling,
    admin: ptBRAdmin,
    preview: ptBRPreview,
    users: ptBRUsers,
    profiles: ptBRProfiles,
    apiKeys: ptBRApiKeys,
    voiceSamples: ptBRVoiceSamples,
    dashboard: ptBRDashboard,
    imports: ptBRImports,
    datasets: ptBRDatasets,
    functions: ptBRFunctions,
    appProfiles: ptBRAppProfiles,
    journeyProfiles: ptBRJourneyProfiles,
    devices: ptBRDevices,
    observability: ptBRObservability,
  },
} as const

// Garante que a inicialização aconteça uma única vez mesmo se este módulo
// for importado por mais de uma entrada (ex.: testes + app).
if (!i18n.isInitialized) {
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      fallbackLng: DEFAULT_LANGUAGE,
      defaultNS: 'common',
      ns: NAMESPACES as unknown as string[],
      supportedLngs: ['pt-BR'],
      interpolation: {
        // React já faz escape de strings interpoladas no JSX
        escapeValue: false,
      },
      detection: {
        // Ordem de detecção: localStorage → navegador → htmlTag.
        order: ['localStorage', 'navigator', 'htmlTag'],
        // Persistência da escolha do usuário (quando o seletor de idioma
        // for adicionado em uma tarefa futura).
        caches: ['localStorage'],
        lookupLocalStorage: 'itap-ura-language',
      },
      returnNull: false,
      // Em produção, evita logs verbosos.
      debug: false,
    })
}

export default i18n
